import { ArrowForward, LinkOutlined } from '@mui/icons-material'
import {
    Avatar,
    Box,
    Card,
    Link as MuiLink,
    Stack,
    Typography,
} from '@mui/material'
import { type GetCompaniesQuery } from '__generated__/graphql'
import { type FC } from 'react'
import { Link } from 'react-router-dom'
import IndustryOrSectorList from 'recruiter/components/IndustryOrSectorList'
import { recruiterRouteConfig } from 'recruiter/routes'
import useIsMobile from 'shared/hooks/useIsMobile'
import { getPxAsRem } from 'shared/utils'
import {
    ClientDetails,
    CompanyCardContent,
    MobileClientCardContent,
    NoWrapBox,
} from './styled'

interface CompanyCardProps {
    client: GetCompaniesQuery['companiesByName']['data'][number]
}

const CompanyCard: FC<CompanyCardProps> = ({ client }) => {
    const profileImage = client.profileImageKey || ''
    const companyName = client.value || '-'
    const companyUrl = client.url || ''
    const headquartersLocation =
        client.headquarters?.officeLocation?.value || '-'
    const isMobile = useIsMobile()

    const companyDetailUrl = `${recruiterRouteConfig.companies.path}/${client.id}`
    const websiteUrl = companyUrl.startsWith('http')
        ? companyUrl
        : `https://${companyUrl}`

    return isMobile ? (
        <Card sx={{ width: '100%', position: 'relative' }}>
            <MobileClientCardContent>
                <ClientDetails variant="h6" sx={{ flexGrow: 1, mt: 1 }}>
                    {client.value}
                </ClientDetails>
                {companyUrl && (
                    <MuiLink
                        href={websiteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            position: 'relative',
                            zIndex: 1,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            '&:hover': {
                                backgroundColor: 'action.hover',
                            },
                        }}
                    >
                        <LinkOutlined color="info" />
                    </MuiLink>
                )}
            </MobileClientCardContent>
            <Link
                to={companyDetailUrl}
                style={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 0,
                }}
                aria-label={`View ${companyName} details`}
            />
        </Card>
    ) : (
        <Card sx={{ width: '100%', position: 'relative' }}>
            <CompanyCardContent>
                <Stack
                    direction="row"
                    alignItems="center"
                    sx={{ width: '100%' }}
                    gap={getPxAsRem(8)}
                >
                    {/* Company Information */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 2,
                            width: '40%',
                        }}
                    >
                        <Avatar
                            src={profileImage}
                            alt={`${companyName} logo`}
                            sx={{ width: 48, height: 48 }}
                        />
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <Typography variant="h5" sx={{ mt: 1 }}>
                                {companyName}
                            </Typography>
                            {companyUrl && (
                                <MuiLink
                                    href={websiteUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        position: 'relative',
                                        zIndex: 1,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 32,
                                        height: 32,
                                        borderRadius: '50%',
                                        '&:hover': {
                                            backgroundColor: 'action.hover',
                                        },
                                    }}
                                >
                                    <LinkOutlined color="info" />
                                </MuiLink>
                            )}
                        </Box>
                    </Box>

                    {/* HQ Location */}
                    <Box
                        sx={{
                            width: '20%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                        }}
                    >
                        <Typography variant="caption">HQ LOCATION</Typography>
                        <Typography>{headquartersLocation}</Typography>
                    </Box>

                    {/* Industry Information */}
                    <NoWrapBox sx={{ width: '20%' }}>
                        <Typography variant="caption">INDUSTRY</Typography>
                        <IndustryOrSectorList
                            items={client?.industries || []}
                        />
                    </NoWrapBox>

                    {/* Sector Information */}
                    <NoWrapBox sx={{ width: '20%' }}>
                        <Typography variant="caption">SECTORS</Typography>
                        <IndustryOrSectorList items={client?.sectors || []} />
                    </NoWrapBox>

                    <ArrowForward />
                </Stack>
            </CompanyCardContent>
            <Link
                to={companyDetailUrl}
                style={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 0,
                }}
                aria-label={`View ${companyName} details`}
            />
        </Card>
    )
}

export default CompanyCard
